/**
 * FileDropZone - Drag-and-drop file upload area.
 *
 * Wraps children with an invisible drop target that highlights on drag-over.
 * Supports images, PDFs, and text files.
 */

import { useState, useRef, useCallback } from 'react'
import { Upload } from 'lucide-react'

// Supported file types
const SUPPORTED_TYPES = [
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
  'application/pdf',
  'text/plain',
  'text/markdown',
  'text/csv',
]

const MAX_FILE_SIZE = 10 * 1024 * 1024 // 10MB
const MAX_FILES = 5

interface FileDropZoneProps {
  children: React.ReactNode
  onFilesSelected: (files: File[]) => void
  disabled?: boolean
  className?: string
}

export function FileDropZone({
  children,
  onFilesSelected,
  disabled = false,
  className = '',
}: FileDropZoneProps) {
  const [isDragging, setIsDragging] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const dragCounterRef = useRef(0)

  const validateFiles = useCallback((fileList: FileList): File[] => {
    const files: File[] = []

    for (let i = 0; i < Math.min(fileList.length, MAX_FILES); i++) {
      const file = fileList[i]

      // Check type
      if (!SUPPORTED_TYPES.includes(file.type)) {
        console.warn(`Unsupported file type: ${file.type}`)
        continue
      }

      // Check size
      if (file.size > MAX_FILE_SIZE) {
        console.warn(`File too large: ${file.name} (${file.size} bytes)`)
        continue
      }

      files.push(file)
    }

    return files
  }, [])

  const handleDragEnter = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault()
      e.stopPropagation()
      if (disabled) return

      dragCounterRef.current++
      if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
        setIsDragging(true)
      }
    },
    [disabled]
  )

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()

    dragCounterRef.current--
    if (dragCounterRef.current === 0) {
      setIsDragging(false)
    }
  }, [])

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
  }, [])

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault()
      e.stopPropagation()

      setIsDragging(false)
      dragCounterRef.current = 0

      if (disabled) return

      const files = validateFiles(e.dataTransfer.files)
      if (files.length > 0) {
        onFilesSelected(files)
      }
    },
    [disabled, onFilesSelected, validateFiles]
  )

  const handleFileInput = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (!e.target.files) return

      const files = validateFiles(e.target.files)
      if (files.length > 0) {
        onFilesSelected(files)
      }

      // Reset input
      e.target.value = ''
    },
    [onFilesSelected, validateFiles]
  )

  const openFilePicker = useCallback(() => {
    fileInputRef.current?.click()
  }, [])

  return (
    <div
      className={`relative ${className}`}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept={SUPPORTED_TYPES.join(',')}
        onChange={handleFileInput}
        className="hidden"
        disabled={disabled}
      />

      {/* Children (the actual input area) */}
      {children}

      {/* Drag overlay */}
      {isDragging && (
        <div className="absolute inset-0 bg-accent/10 border-2 border-dashed border-accent rounded-lg flex items-center justify-center z-10 pointer-events-none">
          <div className="flex flex-col items-center gap-2 text-accent">
            <Upload className="w-8 h-8" />
            <span className="text-sm font-medium">Drop files here</span>
            <span className="text-xs text-text-muted">
              Images, PDFs, or text files (max 10MB)
            </span>
          </div>
        </div>
      )}

      {/* Export openFilePicker for external use */}
      <FileDropZone.OpenPicker ref={fileInputRef} onClick={openFilePicker} />
    </div>
  )
}

// Helper component to expose file picker to parent
FileDropZone.OpenPicker = function OpenPicker({
  ref: _ref,
  onClick,
}: {
  ref: React.RefObject<HTMLInputElement>
  onClick: () => void
}) {
  // Store the onClick handler for external access
  FileDropZone._openPicker = onClick
  return null
}

// Static method to open file picker
FileDropZone._openPicker = () => {}
FileDropZone.open = () => FileDropZone._openPicker()

export default FileDropZone
